import { Button, Select, TextTooltip } from '@components'
import { VAT_PERIOD_OPTIONS } from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { updateBusiness } from '@query'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FaCheck } from 'react-icons/fa'
import styled from 'styled-components'
import {
  ReviewPromptWrapper,
  ReviewPromptCard,
  ReviewPromptTextGroup,
  ReviewPromptActionGroup
} from './ReviewPromptCard'

interface VatPeriodFormValues {
  default_vat_period: number | ''
}

const CONFIRM_PERMISSIONS = PermissionSet.BOOKKEEPING_ADMIN

export const VatPeriodReviewPrompt: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { slug, data: business } = useBusinessContext()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const canConfirm = usePermission(CONFIRM_PERMISSIONS)

  const {
    control,
    handleSubmit,
    formState: { isValid }
  } = useForm<VatPeriodFormValues>({
    values:
      business?.default_vat_period != null
        ? { default_vat_period: business.default_vat_period }
        : { default_vat_period: '' },
    mode: 'onChange'
  })

  const { mutateAsync: update } = useMutation({
    mutationFn: (data: Record<string, unknown>) => updateBusiness({ slug }, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [slug] })
  })

  const [showSpinner, setShowSpinner] = useState(false)

  const onConfirm = handleSubmit(async (values: VatPeriodFormValues) => {
    if (!canConfirm) return

    setShowSpinner(true)
    const minDuration = new Promise(r => setTimeout(r, 400))

    try {
      const payload: Record<string, unknown> = { vat_period_needs_review: false }
      if (
        values.default_vat_period !== '' &&
        values.default_vat_period !== business?.default_vat_period
      ) {
        payload.default_vat_period = values.default_vat_period
      }
      await update(payload)
    } catch {
      toast.error(t('general.error'))
    } finally {
      await minDuration
      setShowSpinner(false)
    }
  })

  if (!business?.vat_period_needs_review) {
    return <>{children}</>
  }

  const confirmButton = (
    <Button
      type="button"
      intent="primary"
      icon={<FaCheck />}
      onClick={onConfirm}
      showSpinner={showSpinner}
      disabled={!canConfirm || showSpinner || !isValid}
    >
      {t('vatPeriodReview.confirm')}
    </Button>
  )

  return (
    <ReviewPromptWrapper>
      <ReviewPromptCard>
        <ReviewPromptTextGroup>
          <h2>{t('vatPeriodReview.title')}</h2>
          <p>{t('vatPeriodReview.description')}</p>
        </ReviewPromptTextGroup>
        <ReviewPromptActionGroup>
          <FieldGroup>
            <Controller
              control={control}
              name="default_vat_period"
              rules={{ required: true }}
              render={({ field: { ref, value, onChange, onBlur, name } }) => (
                <Select
                  ref={ref}
                  name={name}
                  label={t('vatPeriodReview.vatPeriod')}
                  value={value}
                  onChange={e => onChange(Number(e.target.value))}
                  onBlur={onBlur}
                  disabled={!canConfirm}
                >
                  {VAT_PERIOD_OPTIONS.map(({ labelKey, value }) => (
                    <option key={value} value={value}>
                      {t(labelKey)}
                    </option>
                  ))}
                </Select>
              )}
            />
          </FieldGroup>
          {canConfirm ? (
            confirmButton
          ) : (
            <TextTooltip tooltip={t('vatPeriodReview.missingPermissionTooltip')}>
              <DisabledConfirmWrapper>{confirmButton}</DisabledConfirmWrapper>
            </TextTooltip>
          )}
        </ReviewPromptActionGroup>
      </ReviewPromptCard>
    </ReviewPromptWrapper>
  )
}

const FieldGroup = styled.div`
  width: 100%;
`

const DisabledConfirmWrapper = styled.span`
  display: inline-flex;
  cursor: not-allowed;
`
